import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "wouter";
import {
  useSearchEmails,
  getSearchEmailsQueryKey,
  useListTemplates,
} from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Search as SearchIcon,
  Send,
  Inbox as InboxIcon,
  Mail as MailIcon,
  Paperclip,
  X,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { highlightText } from "@/lib/highlight";
import { VerifiedSenderBadge } from "@/components/VerifiedSenderBadge";
import { FailedVerificationWarning } from "@/components/FailedVerificationWarning";
import type { VerificationStatus } from "@/components/VerifiedSenderBadge";
import { useTrustedSenderCheck } from "@/hooks/useTrustedSenderCheck";

function TrustedSenderGate({
  fromAddress,
  verificationStatus,
  testIdSuffix,
}: {
  fromAddress: string | null | undefined;
  verificationStatus: VerificationStatus;
  testIdSuffix: string;
}) {
  const { isTrusted } = useTrustedSenderCheck(fromAddress, verificationStatus);
  if (isTrusted) return null;
  return (
    <FailedVerificationWarning
      verificationStatus={verificationStatus}
      testIdSuffix={testIdSuffix}
    />
  );
}

type Direction = "all" | "sent" | "inbound" | "gmail";
type Status = "" | "sent" | "failed" | "queued" | "bounced" | "complained";

const STATUS_LABEL: Record<Exclude<Status, "">, string> = {
  sent: "Delivered",
  failed: "Failed",
  queued: "Queued",
  bounced: "Bounced",
  complained: "Complained",
};

/**
 * Tiny debounce so we don't issue a fresh network request on every
 * keystroke. 250ms feels instant for users typing through a query but
 * collapses bursts of intermediate states into one request.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Build the detail-page URL for a given search result. The hit row
 * itself just links here — there's no inline detail view, so picking
 * a result hands off to the existing log/inbox/gmail detail pages
 * and they own the open/read state from there.
 */
function detailHrefFor(item: {
  kind: "sent" | "inbound" | "gmail";
  id: string;
}): string {
  if (item.kind === "sent") return `/app/logs/${item.id}`;
  if (item.kind === "gmail") return `/app/inbox/gmail/${item.id}`;
  return `/app/inbox/${item.id}`;
}

function DirectionBadge({ kind }: { kind: "sent" | "inbound" | "gmail" }) {
  if (kind === "sent") {
    return (
      <Badge
        variant="default"
        className="gap-1"
        data-testid="badge-direction-sent"
      >
        <Send className="h-3 w-3" />
        Sent
      </Badge>
    );
  }
  if (kind === "gmail") {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-red-500/40 text-red-700 dark:text-red-400"
        data-testid="badge-direction-gmail"
      >
        <MailIcon className="h-3 w-3" />
        Gmail
      </Badge>
    );
  }
  return (
    <Badge
      variant="secondary"
      className="gap-1"
      data-testid="badge-direction-inbound"
    >
      <InboxIcon className="h-3 w-3" />
      Inbox
    </Badge>
  );
}

const PAGE_SIZE = 25;

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [direction, setDirection] = useState<Direction>("all");
  const [status, setStatus] = useState<Status>("");
  const [templateId, setTemplateId] = useState<string>("");
  const [hasAttachment, setHasAttachment] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [offset, setOffset] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset to page 0 whenever any filter / query changes — staying on
  // page 5 of stale results would be confusing.
  useEffect(() => {
    setOffset(0);
  }, [q, direction, status, templateId, hasAttachment, dateFrom, dateTo]);

  const debouncedQ = useDebouncedValue(q, 250);

  // Pull the user's templates so the dropdown is real instead of a
  // free-text id box. Failure is non-fatal — the rest of the page
  // works without it.
  const { data: templatesData } = useListTemplates(
    {},
    { query: { queryKey: ["search-templates", "list"] } },
  );
  const templates: Array<{ id: number; name: string }> = templatesData ?? [];

  // Convert the date pickers (which give us YYYY-MM-DD) into ISO
  // timestamps at the start / end of the chosen day in UTC. Empty
  // strings stay undefined so the API skips the filter entirely.
  const params = useMemo(() => {
    const p: Parameters<typeof useSearchEmails>[0] = {
      direction,
      limit: PAGE_SIZE,
      offset,
    };
    if (debouncedQ.trim().length > 0) p.q = debouncedQ.trim();
    if (status) p.status = status;
    if (templateId) p.templateId = Number(templateId);
    if (hasAttachment) p.hasAttachment = true;
    if (dateFrom) p.dateFrom = `${dateFrom}T00:00:00.000Z`;
    if (dateTo) p.dateTo = `${dateTo}T23:59:59.999Z`;
    return p;
  }, [
    debouncedQ,
    direction,
    status,
    templateId,
    hasAttachment,
    dateFrom,
    dateTo,
    offset,
  ]);

  const { data, isLoading, isFetching, error } = useSearchEmails(params, {
    query: {
      queryKey: getSearchEmailsQueryKey(params),
      // Keep the previous results visible during refetches so the
      // page doesn't flash empty between keystrokes.
      placeholderData: (prev) => prev,
    },
  });

  // Auto-focus the search box on mount so a user landing here via the
  // "/" shortcut (or the nav entry) can start typing immediately.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function clearAllFilters() {
    setQ("");
    setDirection("all");
    setStatus("");
    setTemplateId("");
    setHasAttachment(false);
    setDateFrom("");
    setDateTo("");
    inputRef.current?.focus();
  }

  // Active-filter summary chips — shown above the result list so a
  // user can see (and individually clear) what's currently narrowing
  // their results without scanning the whole filter card.
  type Chip = { key: string; label: string; onClear: () => void };
  const chips: Chip[] = [];
  if (direction !== "all") {
    chips.push({
      key: "direction",
      label: `Direction: ${
        direction === "sent"
          ? "Sent"
          : direction === "inbound"
            ? "Inbox"
            : "Gmail"
      }`,
      onClear: () => setDirection("all"),
    });
  }
  if (status) {
    chips.push({
      key: "status",
      label: `Status: ${STATUS_LABEL[status]}`,
      onClear: () => setStatus(""),
    });
  }
  if (templateId) {
    const tpl = templates.find((t) => String(t.id) === templateId);
    chips.push({
      key: "template",
      label: `Template: ${tpl?.name ?? templateId}`,
      onClear: () => setTemplateId(""),
    });
  }
  if (hasAttachment) {
    chips.push({
      key: "attach",
      label: "Has attachment",
      onClear: () => setHasAttachment(false),
    });
  }
  if (dateFrom) {
    chips.push({
      key: "datefrom",
      label: `From: ${dateFrom}`,
      onClear: () => setDateFrom(""),
    });
  }
  if (dateTo) {
    chips.push({
      key: "dateto",
      label: `To: ${dateTo}`,
      onClear: () => setDateTo(""),
    });
  }

  const hasAnyFilter = chips.length > 0 || q.length > 0;

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const gmailIncluded = data?.gmailIncluded ?? false;
  const hasMore = items.length === PAGE_SIZE;

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Search</h1>
          <p className="text-muted-foreground">
            Search across your sent mail, the inbox, and your connected Gmail.
            Press <kbd className="rounded border bg-muted px-1.5 py-0.5 text-xs">/</kbd>{" "}
            anywhere to jump back here.
          </p>
        </div>

        <Card className="p-4 space-y-4">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search subject, body, or addresses…"
              className="pl-9"
              data-testid="input-search-query"
              aria-label="Search query"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Direction
              </label>
              <Select
                value={direction}
                onValueChange={(v) => setDirection(v as Direction)}
              >
                <SelectTrigger data-testid="select-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="inbound">Inbox</SelectItem>
                  <SelectItem value="gmail">Gmail</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Status (sent only)
              </label>
              <Select
                value={status === "" ? "any" : status}
                onValueChange={(v) =>
                  setStatus(v === "any" ? "" : (v as Status))
                }
              >
                <SelectTrigger data-testid="select-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any status</SelectItem>
                  <SelectItem value="sent">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="bounced">Bounced</SelectItem>
                  <SelectItem value="complained">Complained</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Template (sent only)
              </label>
              <Select
                value={templateId === "" ? "any" : templateId}
                onValueChange={(v) => setTemplateId(v === "any" ? "" : v)}
              >
                <SelectTrigger data-testid="select-template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any template</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">
                From date
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="input-date-from"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">
                To date
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="input-date-to"
              />
            </div>

            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={hasAttachment}
                  onChange={(e) => setHasAttachment(e.target.checked)}
                  className="h-4 w-4"
                  data-testid="checkbox-has-attachment"
                />
                Has attachment
              </label>
            </div>
          </div>

          {hasAnyFilter && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              <span className="text-xs text-muted-foreground">
                Active filters:
              </span>
              {chips.map((c) => (
                <Badge
                  key={c.key}
                  variant="secondary"
                  className="gap-1"
                  data-testid={`chip-${c.key}`}
                >
                  {c.label}
                  <button
                    type="button"
                    onClick={c.onClear}
                    className="ml-1 hover:text-destructive"
                    aria-label={`Clear ${c.label}`}
                    data-testid={`chip-clear-${c.key}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="ml-auto"
                data-testid="button-clear-filters"
              >
                Clear all
              </Button>
            </div>
          )}
        </Card>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
            Search failed. Please try again.
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span data-testid="text-result-count">
            {isLoading
              ? "Searching…"
              : `${total.toLocaleString()} match${total === 1 ? "" : "es"}${
                  gmailIncluded ? " (plus Gmail)" : ""
                }`}
          </span>
          {isFetching && !isLoading && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
        </div>

        <div className="space-y-2" data-testid="list-search-results">
          {items.length === 0 && !isLoading ? (
            <Card className="p-8 text-center text-muted-foreground">
              No results.
            </Card>
          ) : (
            items.map((item) => (
              <Link
                key={`${item.kind}:${item.id}`}
                href={detailHrefFor(item)}
                className="block"
                data-testid={`row-result-${item.kind}-${item.id}`}
              >
                <Card className="p-4 hover:bg-accent/50 transition-colors cursor-pointer">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <DirectionBadge kind={item.kind} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium truncate">
                          {item.subject
                            ? highlightText(item.subject, debouncedQ)
                            : "(no subject)"}
                        </h3>
                        {item.hasAttachment && (
                          <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        )}
                        {item.templateName && (
                          <Badge variant="outline" className="text-xs">
                            {item.templateName}
                          </Badge>
                        )}
                        {item.status && item.status !== "sent" && (
                          <Badge
                            variant={
                              item.status === "failed"
                                ? "destructive"
                                : "secondary"
                            }
                            className="text-xs"
                          >
                            {item.status}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5 truncate">
                        <span className="truncate">
                          {item.kind === "sent"
                            ? `to ${item.toAddress}`
                            : `from ${item.fromName ?? item.fromAddress}`}
                          {item.mailboxAddress && ` • ${item.mailboxAddress}`}
                        </span>
                        {(item.kind === "gmail" || item.kind === "inbound") && (
                          <>
                            <VerifiedSenderBadge
                              verificationStatus={item.verificationStatus ?? null}
                              testIdSuffix={`-search-${item.kind}-${item.id}`}
                            />
                            <TrustedSenderGate
                              fromAddress={item.fromAddress}
                              verificationStatus={item.verificationStatus ?? "unverified"}
                              testIdSuffix={`-search-${item.kind}-${item.id}`}
                            />
                          </>
                        )}
                      </div>
                      {item.snippet && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {highlightText(item.snippet, debouncedQ)}
                        </p>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(item.occurredAt), {
                        addSuffix: true,
                      })}
                    </div>
                  </div>
                </Card>
              </Link>
            ))
          )}
        </div>

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0 || isFetching}
            data-testid="button-prev-page"
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {Math.floor(offset / PAGE_SIZE) + 1}
          </span>
          <Button
            variant="outline"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!hasMore || isFetching}
            data-testid="button-next-page"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
